import React, { useState, useEffect } from "react";
import emailjs from '@emailjs/browser';
import './App.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faMapMarkerAlt, faPhone, faEnvelope, faArrowUpRightFromSquare, faBars, faTimes, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faGithub, faFacebookF, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';

// Your portfolio data
const MY_PORTFOLIO_DATA = {
  name: "Abd Razagh Mohamed Nafaa Babana",
  title: "Développeur Full-Stack & Mobile",
  profile: "Développeur Full-Stack & Mobile avec une solide expérience dans la conception d'applications de production avec Flutter, React et Django. Passionné par la création de solutions innovantes, l'optimisation des performances et le déploiement d'architectures robustes pour des organisations d'envergure.",
  experience: [
    {
      title: "Stagiaire Développeur Mobile",
      company: "MITC",
      period: "Nov. 2025 - Aujourd'hui",
      description: "Conception et déploiement d'applications mobiles robustes avec Flutter. Focus sur l'optimisation des performances et l'intégration fluide de services cloud pour une expérience utilisateur de premier plan."
    },
    {
      title: "Stagiaire Développeur Full-Stack",
      company: "Lebjawi Tech",
      period: "Avr. 2025 - Nov. 2025",
      description: "Développement d'applications web et mobiles réactives. Collaboration sur des projets complexes, assurant la qualité du code et l'optimisation des ressources système."
    },
    {
      title: "Stagiaire Développeur Logiciel",
      company: "Banque Centrale de Mauritanie",
      period: "Déc. 2024 - Jan. 2025",
      description: "Mise en place d'une solution de monitoring en temps réel pour la surveillance des alertes critiques. Développement de l'architecture backend et d'interfaces de gestion modernes."
    },
    {
      title: "Stagiaire Développeur Logiciel",
      company: "SupNum",
      period: "2023 - 2024",
      description: "Architecture et développement d'un système de gestion automatisé. Implémentation de protocoles de sécurité avancés et d'APIs REST hautement performantes."
    }
  ],
  education: [
    {
      degree: "Master en Informatique Appliquée à la Gestion",
      school: "Institut Supérieur de Comptabilité et d'Administration des Entreprises (ISCAE)",
      year: "2023"
    },
    {
      degree: "Licence en Réseaux Informatiques et Télécommunications",
      school: "Institut Supérieur de Comptabilité et d'Administration des Entreprises (ISCAE)",
      year: "2021"
    }
  ],
  projects: [
    {
      title: "Alert Management System",
      description: "Solution de monitoring en temps réel pour la surveillance des systèmes critiques, offrant une visibilité immédiate sur les alertes opérationnelles.",
      tech: ["Django", "React", "PostgreSQL"],
      url: "https://github.com/AbdouNavaa/BCMA",
      images: [
        'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=600&q=80'
      ]
    },
    {
      title: "Teacher Payment Management",
      description: "Système d'automatisation des flux de paiement, intégrant des mécanismes de sécurité robustes et une gestion simplifiée des transactions.",
      tech: ["Node.js", "Angular", "Flutter", "JWT"],
      images: [
        'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=600&q=80'
      ]
    },
    {
      title: "Cars WebSite",
      description: "Plateforme moderne de présentation et de gestion de véhicules, mettant l'accent sur une interface utilisateur fluide et une expérience de navigation immersive.",
      tech: ["React", "CSS", "Framer Motion"],
      url: "https://github.com/AbdouNavaa/cars_webSite",
      images: [
        process.env.PUBLIC_URL + '/cars_webSite1.png',
        process.env.PUBLIC_URL + '/cars_webSite2.png',
        process.env.PUBLIC_URL + '/cars_webSite3.png'
      ]
    },
    {
      title: "E-commerce Mobile",
      description: "Application mobile e-commerce complète avec gestion des produits, panier et authentification sécurisée.",
      tech: ["Node.js", "Flutter", "MongoDB"],
      url: "https://github.com/AbdouNavaa/ecomm_mobile",
      images: [
        process.env.PUBLIC_URL + '/ec-mobile1.jpeg',
        process.env.PUBLIC_URL + '/ec-mobile2.jpeg',
        process.env.PUBLIC_URL + '/ec-mobile3.jpeg'
      ]
    }
  ],
  stats: [
    { value: "3+", label: "Years of Experience" },
    { value: "10+", label: "Projects Completed" },
    { value: "5+", label: "Technologies Mastered" },
  ],
  contact: {
    address: "Arafat, Nouakchott, Mauritanie",
    phone: "+222 41445145 - +222 26387658",
    email: "babana9977@gmail.com",
    linkedin: "https://www.linkedin.com/in/abd-razak-babana-983336281",
    github: "https://github.com/AbdouNavaa"
  },
  socialLinks: [
    { icon: faFacebookF, url: "https://facebook.com" },
    { icon: faLinkedin, url: "https://www.linkedin.com/in/abd-razak-babana-983336281" },
    { icon: faWhatsapp, url: "https://wa.me/22226387658" },
    { icon: faGithub, url: "https://github.com/AbdouNavaa" },
  ],
};

// Translations
const translations = {
  en: {
    name: "Abd Razagh Mohamed Nafaa Babana",
    i_am: "I am",
    personal: "Personal",
    my_desc: "Full-Stack & Mobile Developer with a solid background in designing production applications using Flutter, React, and Django. Passionate about creating innovative solutions, optimizing performance, and deploying robust architectures for large-scale organizations.",
    services: "My Quality Services",
    works: "My Recent Works",
    resume: "Resume",
    skills: "My Skills",
    contact: "Contact",
    downloadCV: "Download CV",
    languages: "Languages",
    languages_list: [
      { name: "Arabic", level: "Native Language" },
      { name: "French", level: "Professional Proficiency" },
      { name: "English", level: "Professional Proficiency" }
    ],
    "Years of Experience": "Years of Experience",
    "Projects Completed": "Projects Completed",
    "Technologies Mastered": "Technologies Mastered",
    exp_titles: ["Mobile Developer Intern", "Full-Stack Developer Intern", "Software Developer Intern", "Software Developer Intern"],
    exp_descs: [
      "Design and deployment of robust mobile applications using Flutter. Focus on performance optimization and seamless cloud service integration for a premium user experience.",
      "Development of responsive web and mobile applications. Collaboration on complex projects, ensuring code quality and system resource optimization.",
      "Implementation of a real-time monitoring solution for critical alert surveillance. Backend architecture development and modern management interface design.",
      "Architecture and development of an automated management system. Implementation of advanced security protocols and high-performance REST APIs."
    ],
    proj_titles: ["Alert Management System", "Teacher Payment Management", "Cars WebSite", "E-commerce Mobile"],
    proj_descs: [
      "Real-time monitoring solution for critical system surveillance, providing immediate visibility into operational alerts.",
      "Payment flow automation system, integrating robust security mechanisms and simplified transaction management.",
      "Modern platform for vehicle presentation and management, emphasizing a fluid user interface and immersive browsing experience.",
      "Complete mobile e-commerce application with product management, shopping cart, and secure authentication."
    ],
    profile_title: "Full-Stack & Mobile Developer",
    service_titles: ["Web Development", "Mobile Development", "Backend Systems", "Database Management"],
    service_descs: [
      "Creating modern and responsive web applications using the latest technologies.",
      "Developing cross-platform mobile applications with Flutter.",
      "Designing robust APIs with Node.js and Django.",
      "Optimized MySQL and MongoDB solutions for high performance."
    ],
    form_name: "Full Name",
    form_email: "Email Address",
    form_subject: "Project Subject",
    form_message: "Your Message",
    form_send: "Send Message",
    form_sending: "Sending...",
    form_success: "Message sent successfully.",
    form_error: "Something went wrong. Please try again.",
    contact_subtitle: "Let's work together"
  },
  fr: {
    name: "Abd Razagh Mohamed Nafaa Babana",
    i_am: "Je suis",
    personal: "Personnel",
    my_desc: "Développeur Full-Stack & Mobile avec une solide expérience dans la conception d'applications de production avec Flutter, React et Django. Passionné par la création de solutions innovantes, l'optimisation des performances et le déploiement d'architectures robustes pour des organisations d'envergure.",
    services: "Mes Services de Qualité",
    works: "Mes Récentes Réalisations",
    resume: "Résumé",
    skills: "Mes Compétences",
    contact: "Contact",
    downloadCV: "Télécharger le CV",
    languages: "Langues",
    languages_list: [
      { name: "Arabe", level: "Langue maternelle" },
      { name: "Français", level: "Compétence professionnelle" },
      { name: "Anglais", level: "Compétence professionnelle" }
    ],
    "Years of Experience": "Années d'Expérience",
    "Projects Completed": "Projets Complétés",
    "Technologies Mastered": "Technologies Maîtrisées",
    exp_titles: ["Stagiaire Développeur Mobile", "Stagiaire Développeur Full-Stack", "Stagiaire Développeur Logiciel", "Stagiaire Développeur Logiciel"],
    exp_descs: [
      "Conception et déploiement d'applications mobiles robustes avec Flutter. Focus sur l'optimisation des performances et l'intégration fluide de services cloud pour une expérience utilisateur de premier plan.",
      "Développement d'applications web et mobiles réactives. Collaboration sur des projets complexes, assurant la qualité du code et l'optimisation des ressources système.",
      "Mise en place d'une solution de monitoring en temps réel pour la surveillance des alertes critiques. Développement de l'architecture backend et d'interfaces de gestion modernes.",
      "Architecture et développement d'un système de gestion automatisé. Implémentation de protocoles de sécurité avancés et d'APIs REST hautement performantes."
    ],
    proj_titles: ["Système de gestion des alertes", "Gestion des paiements enseignants", "Cars WebSite", "E-commerce Mobile"],
    proj_descs: [
      "Solution de monitoring en temps réel pour la surveillance des systèmes critiques, offrant une visibilité immédiate sur les alertes opérationnelles.",
      "Système d'automatisation des flux de paiement, intégrant des mécanismes de sécurité robustes et une gestion simplifiée des transactions.",
      "Plateforme moderne de présentation et de gestion de véhicules, mettant l'accent sur une interface utilisateur fluide et une expérience de navigation immersive.",
      "Application mobile e-commerce complète avec gestion des produits, panier et authentification sécurisée."
    ],
    profile_title: "Développeur Full-Stack & Mobile",
    service_titles: ["Développement Web", "Développement Mobile", "Systèmes Backend", "Gestion de Bases de Données"],
    service_descs: [
      "Création d'applications web modernes et réactives avec les dernières technologies.",
      "Développement d'applications mobiles multiplateformes avec Flutter.",
      "Conception d'API robustes avec Node.js et Django.",
      "Solutions MySQL et MongoDB optimisées pour la performance."
    ],
    form_name: "Nom Complet",
    form_email: "Adresse Email",
    form_subject: "Sujet du Projet",
    form_message: "Votre Message",
    form_send: "Envoyer le Message",
    form_sending: "Envoi en cours...",
    form_success: "Message envoyé avec succès.",
    form_error: "Une erreur est survenue. Veuillez réessayer.",
    contact_subtitle: "Travaillons ensemble"
  },
  ar: {
    name: "عبد الرزاق محمد نافع بابانا",
    i_am: "أنا",
    personal: "شخصي",
    my_desc: "مطور تطبيقات ويب وجوال يتمتع بخبرة قوية في تصميم تطبيقات الإنتاج باستخدام Flutter و React و Django. شغوف بابتكار حلول تقنية، تحسين الأداء، ونشر بنيات برمجية قوية للمؤسسات الكبرى.",
    services: "خدماتي",
    works: "أعمالي الأخيرة",
    resume: "السيرة الذاتية",
    skills: "مهاراتي",
    contact: "اتصل بي",
    downloadCV: "تحميل السيرة الذاتية",
    languages: "اللغات",
    languages_list: [
      { name: "العربية", level: "اللغة الأم" },
      { name: "الفرنسية", level: "إتقان مهني" },
      { name: "الإنجليزية", level: "إتقان مهني" }
    ],
    "Years of Experience": "سنوات الخبرة",
    "Projects Completed": "المشاريع المكتملة",
    "Technologies Mastered": "التقنيات المتقنة",
    exp_titles: ["متدرب مطور جوال", "متدرب مطور ويب وجوال", "متدرب مطور برمجيات", "متدرب مطور برمجيات"],
    exp_descs: [
      "تصميم ونشر تطبيقات جوال قوية باستخدام Flutter. التركيز على تحسين الأداء والتكامل السلس للخدمات السحابية لتوفير تجربة مستخدم ممتازة.",
      "تطوير تطبيقات ويب وجوال متجاوبة. التعاون في مشاريع معقدة مع ضمان جودة الكود وتحسين موارد النظام.",
      "تنفيذ حل مراقبة في الوقت الفعلي لمتابعة التنبيهات الحرجة. تطوير بنية الخلفية وتصميم واجهات إدارة حديثة.",
      "هندسة وتطوير نظام إدارة مؤتمت. تنفيذ بروتوكولات أمان متقدمة وواجهات برمجة تطبيقات REST عالية الأداء."
    ],
    proj_titles: ["نظام إدارة التنبيهات", "إدارة مدفوعات المعلمين", "موقع عرض السيارات", "تطبيق تجارة إلكترونية"],
    proj_descs: [
      "حل مراقبة في الوقت الفعلي لمراقبة الأنظمة الحرجة، مما يوفر رؤية فورية للتنبيهات التشغيلية.",
      "نظام أتمتة تدفقات الدفع، يدمج آليات أمان قوية وإدارة مبسطة للمعاملات.",
      "منصة حديثة لعرض وإدارة السيارات، مع التركيز على واجهة مستخدم سلسة وتجربة تصفح غامرة.",
      "تطبيق جوال متكامل للتجارة الإلكترونية مع إدارة المنتجات وسلة التسوق والمصادقة الآمنة."
    ],
    profile_title: "مطور ويب وجوال",
    service_titles: ["تطوير الويب", "تطوير تطبيقات الجوال", "أنظمة الخلفية", "إدارة قواعد البيانات"],
    service_descs: [
      "إنشاء تطبيقات ويب حديثة ومتجاوبة باستخدام أحدث التقنيات.",
      "تطوير تطبيقات جوال متعددة المنصات باستخدام Flutter.",
      "تصميم واجهات برمجة تطبيقات (API) قوية باستخدام Node.js و Django.",
      "حلول MySQL و MongoDB محسنة للأداء العالي."
    ],
    form_name: "الاسم الكامل",
    form_email: "البريد الإلكتروني",
    form_subject: "موضوع المشروع",
    form_message: "رسالتك",
    form_send: "إرسال الرسالة",
    form_sending: "جاري الإرسال...",
    form_success: "تم إرسال الرسالة بنجاح.",
    form_error: "حدث خطأ ما. يرجى المحاولة مرة أخرى.",
    contact_subtitle: "لنعمل معاً"
  },
};

// Animations
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Custom Components
const LanguageSwitcher = ({ lang, setLang, isMobile }) => {
  const options = [
    { code: 'en', label: 'English', icon: '🇺🇸' },
    { code: 'fr', label: 'Français', icon: '🇫🇷' },
    { code: 'ar', label: 'العربية', icon: '🇲🇷' }
  ];

  const current = options.find(o => o.code === lang);

  return (
    <div className={`luxury-lang-switcher ${isMobile ? 'is-mobile' : ''}`}>
      <div className="lang-trigger">
        {/* <span className="lang-icon">{current.icon}</span> */}
        <span className="lang-code">{current.code.toUpperCase()}</span>
        <FontAwesomeIcon icon={faChevronDown} className="chevron" />
      </div>
      <div className="lang-dropdown-menu">
        {options.map((opt) => (
          <div
            key={opt.code}
            className={`lang-menu-item ${lang === opt.code ? 'active' : ''}`}
            onClick={() => setLang(opt.code)}
          >
            <span className="opt-icon">{opt.icon}</span>
            <span className="opt-label">{opt.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Mobile Drawer Component
const MobileDrawer = ({ isOpen, onClose, t, setLang, lang, theme, toggleTheme }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="drawer-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="mobile-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          >
            <div className="drawer-header">
              <button className="close-btn" onClick={onClose}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <ul className="drawer-links">
              <li><a href="#services" onClick={onClose}>{t("services")}</a></li>
              <li><a href="#recent-works" onClick={onClose}>{t("works")}</a></li>
              <li><a href="#experience-education" onClick={onClose}>{t("resume")}</a></li>
              <li><a href="#skills-section" onClick={onClose}>{t("skills")}</a></li>
              <li><a href="#contact" onClick={onClose}>{t("contact")}</a></li>
            </ul>
            <div className="drawer-footer">
              <div className="drawer-controls">
                <div className="theme-toggle" onClick={toggleTheme}>
                  {theme === 'dark' ? '🌙' : '☀️'}
                </div>
                <LanguageSwitcher lang={lang} setLang={setLang} isMobile />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Project Image Carousel Component
const ProjectImage = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = React.useState(0);

  useEffect(() => {
    if (!images || images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [images]);

  if (!images || images.length === 0) return <div className="work-image-placeholder" />;

  return (
    <div className="carousel-container" style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      {images.map((img, idx) => (
        <motion.img
          key={idx}
          src={img}
          alt={`${title} ${idx}`}
          className="work-image"
          initial={{ opacity: 0 }}
          animate={{ opacity: currentIndex === idx ? 1 : 0 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          style={{
            position: idx === 0 ? 'relative' : 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      ))}
      {images.length > 1 && (
        <div className="carousel-dots">
          {images.map((_, idx) => (
            <div
              key={idx}
              className={`carousel-dot ${currentIndex === idx ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Recent Works Component
const RecentWorksGrid = ({ data, t, lang }) => {
  return (
    <motion.section
      id="recent-works"
      className="recent-works-section"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeInUp}
    >
      <div className="container">
        <h2 className="section-title">{t("works")}</h2>
        <div className="works-grid">
          {data.projects.map((project, index) => (
            <motion.div
              className="work-card"
              key={index}
              variants={fadeInUp}
            >
              <div className="work-image-wrapper">
                <ProjectImage
                  images={project.images}
                  title={project.title}
                />
              </div>
              <div className="work-info">
                <h3 className="work-title">
                  {t("proj_titles")[index] || project.title}
                </h3>
                <p className="work-desc">{t("proj_descs")[index] || project.description}</p>
                <div className="work-tech">
                  {project.tech.map((tech, i) => (
                    <span key={i} className="tech-tag">{tech}</span>
                  ))}
                </div>
                {project.url && (
                  <a href={project.url} target="_blank" rel="noopener noreferrer" className="project-link">
                    View Project <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

// Header Component
const Header = ({ data, t, lang, setLang, theme, toggleTheme }) => {
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  return (
    <header className="header">
      <div className="container">
        <nav className="navbar">
          <div className="logo">
            <img src={process.env.PUBLIC_URL + '/AR-Dev.jpg'} alt='Logo' />
          </div>
          <ul className="nav-links desktop-only">
            <li><a href="#services">{t("services")}</a></li>
            <li><a href="#recent-works">{t("works")}</a></li>
            <li><a href="#experience-education">{t("resume")}</a></li>
            <li><a href="#skills-section">{t("skills")}</a></li>
            <li><a href="#contact">{t("contact")}</a></li>
          </ul>
          <div className="theme-lang-controls desktop-only">
            <div className="theme-toggle" onClick={toggleTheme}>
              {theme === 'dark' ? '🌙' : '☀️'}
            </div>
            <LanguageSwitcher lang={lang} setLang={setLang} />
          </div>
          <button className="hamburger-btn mobile-only" onClick={() => setDrawerOpen(true)}>
            <FontAwesomeIcon icon={faBars} />
          </button>
        </nav>

        <MobileDrawer
          isOpen={isDrawerOpen}
          onClose={() => setDrawerOpen(false)}
          t={t}
          setLang={setLang}
          lang={lang}
          theme={theme}
          toggleTheme={toggleTheme}
        />

        <div className="hero-section">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, x: lang === 'ar' ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>
              {t("i_am")} <br /><span>{t("name")}</span>
            </h1>
            <p className="hero-subtitle">{t("profile_title")}</p>
            <p className="hero-desc">{t("my_desc")}</p>
            <div className="cta-buttons">
              <a
                className="download-cv-btn"
                href={process.env.PUBLIC_URL + '/MyCV.pdf'}
                download
              >
                <FontAwesomeIcon icon={faDownload} /> {t("downloadCV")}
              </a>

              <div className="social-links">
                {data.socialLinks.map((link, index) => (
                  <a href={link.url} target="_blank" rel="noopener noreferrer" key={index}>
                    <FontAwesomeIcon icon={link.icon} />
                  </a>
                ))}
              </div>
            </div>
            <div className="stats">
              {data.stats.map((stat, index) => (
                <div className="stat-item" key={index}>
                  <h3>{stat.value}</h3>
                  <p>{t(stat.label)}</p>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div
            className="hero-image"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <img src={process.env.PUBLIC_URL + '/myImage.jpg'} alt={data.name} />
          </motion.div>
        </div>
      </div>
    </header>
  );
};

// Services Component
const Services = ({ data, t, lang }) => {
  const serviceIcons = [1, 2, 3, 4];

  return (
    <section id="services" className="services-section">
      <div className="container">
        <motion.h2
          className="section-title"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          {t("services")}
        </motion.h2>
        <motion.div
          className="services-list-design"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          {serviceIcons.map((_, index) => (
            <motion.div className="service-row-design" key={index} variants={fadeInUp}>
              <div className="service-number-circle">{String(index + 1).padStart(2, "0")}</div>
              <h3 className="service-title-design">{t("service_titles")[index]}</h3>
              <p className="service-desc-design">{t("service_descs")[index]}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Experience and Education Component
const ExperienceEducation = ({ data, t, lang }) => {
  return (
    <section id="experience-education" className="experience-education-section">
      <div className="container">
        <div className="edu-exp-grid">
          <motion.div
            className="experience-column"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="section-title-left">
              {lang === "fr" ? "Expériences Professionnelles" : lang === "ar" ? "الخبرات المهنية" : "Professional Experience"}
            </h2>
            {data.experience.map((exp, index) => (
              <div className="timeline-item" key={index}>
                <p className="timeline-period">{exp.period}</p>
                <h3 className="timeline-title">{t("exp_titles")[index] || exp.title}</h3>
                <p className="timeline-company">{exp.company}</p>
                <p className="timeline-desc">{t("exp_descs")[index] || exp.description}</p>
              </div>
            ))}
          </motion.div>

          <motion.div
            className="education-column"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="section-title-left">
              {lang === "fr" ? "Éducation" : lang === "ar" ? "التعليم" : "Education"}
            </h2>
            {data.education.map((edu, index) => (
              <div className="timeline-item" key={index}>
                <p className="timeline-period">{edu.year}</p>
                <h3 className="timeline-title">{edu.degree}</h3>
                <p className="timeline-company">{edu.school}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Skills Component
const Skills = ({ t, data }) => {
  const allSkills = [
    { name: "Flutter", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg" },
    { name: "React", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" },
    { name: "Next.js", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg" },
    { name: "Node.js", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg" },
    { name: "Django", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/django/django-plain.svg" },
    { name: "Supabase", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/supabase/supabase-original.svg" },
    { name: "GitHub", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg" },
    { name: "GitLab", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/gitlab/gitlab-original.svg" },
    { name: "Angular", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angularjs/angularjs-original.svg" },
    { name: "Spring Boot", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg" },
    { name: "MySQL", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg" },
    { name: "MongoDB", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg" },
    { name: "Firebase", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg" },
    { name: "Git", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg" },
  ];

  // Split skills into two rows for the marquee
  const firstRow = allSkills.slice(0, Math.ceil(allSkills.length / 2));
  const secondRow = allSkills.slice(Math.ceil(allSkills.length / 2));

  return (
    <section id="skills-section" className="skills-section">
      <div className="container">
        <h2 className="section-title">{t("skills")}</h2>

        <div className="skills-marquee-wrapper">
          {/* Row 1: Moves Left */}
          <div className="skills-marquee-track left-track">
            {[...firstRow, ...firstRow].map((skill, idx) => (
              <div className="skill-card-design" key={`row1-${idx}`}>
                <div className="skill-icon-design">
                  <img src={skill.icon} alt={skill.name} />
                </div>
                <div className="skill-name-design">{skill.name}</div>
              </div>
            ))}
          </div>

          {/* Row 2: Moves Right */}
          <div className="skills-marquee-track right-track">
            {[...secondRow, ...secondRow].map((skill, idx) => (
              <div className="skill-card-design" key={`row2-${idx}`}>
                <div className="skill-icon-design">
                  <img src={skill.icon} alt={skill.name} />
                </div>
                <div className="skill-name-design">{skill.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Component
const Contact = ({ data, t, lang }) => {
  const [formState, setFormState] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setError(false);

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          name: formState.name,   // Changed from from_name
          email: formState.email, // Changed from from_email
          reply_to: formState.email,
          message: formState.message,
          time: new Date().toLocaleString(),
          to_name: data.name,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      setSubmitted(true);
      setFormState({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      console.error('EmailJS Error:', err);
      setError(true);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="container">
        <h2 className="section-title">{t("contact")}</h2>
        {/* <p className="contact-subtitle">{t("contact_subtitle")}</p> */}

        <div className="contact-wrapper-minimal">
          <div className="contact-info-minimal">
            <div className="info-item-minimal">
              <span className="info-icon-minimal"><FontAwesomeIcon icon={faEnvelope} /></span>
              <p>{data.contact.email}</p>
            </div>
            <div className="info-item-minimal">
              <span className="info-icon-minimal"><FontAwesomeIcon icon={faPhone} /></span>
              <p>{data.contact.phone}</p>
            </div>
            <div className="info-item-minimal">
              <span className="info-icon-minimal"><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
              <p>{data.contact.address}</p>
            </div>
          </div>

          <form className="contact-form-refined" onSubmit={handleSubmit}>
            <div className="form-grid-minimal">
              <input
                type="text"
                placeholder={t("form_name")}
                required
                value={formState.name}
                onChange={(e) => setFormState({ ...formState, name: e.target.value })}
              />
              <input
                type="email"
                placeholder={t("form_email")}
                required
                value={formState.email}
                onChange={(e) => setFormState({ ...formState, email: e.target.value })}
              />
            </div>
            <textarea
              placeholder={t("form_message")}
              rows="5"
              required
              value={formState.message}
              onChange={(e) => setFormState({ ...formState, message: e.target.value })}
            ></textarea>
            <button type="submit" className="submit-btn-minimal" disabled={isSending}>
              {isSending ? t("form_sending") : t("form_send")}
            </button>
            {submitted && <p className="form-success-msg-minimal">{t("form_success")}</p>}
            {error && <p className="form-error-msg-minimal" style={{ color: '#ef4444', marginTop: '1rem', textAlign: 'center' }}>{t("form_error")}</p>}
          </form>
        </div>
      </div>
    </section>
  );
};

// Languages Component
const Languages = ({ data, t }) => {
  return (
    <section id="languages" className="languages-section">
      <div className="container">
        <h2 className="section-title">{t("languages")}</h2>
        <div className="languages-minimal-grid">
          {t("languages_list").map((lang, index) => (
            <div className="lang-minimal-card" key={index}>
              <div className="lang-icon-sphere">{lang.name[0]}</div>
              <h3>{lang.name}</h3>
              <p>{lang.level}</p>
              <div className="minimal-bar">
                <div
                  className="minimal-fill"
                  style={{ width: lang.name === 'Arabic' || lang.name === 'Arabe' ? '100%' : '85%' }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = ({ data, t, lang }) => {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-top">
          <div className="footer-brand">
            <div className="footer-logo">
              <img src={process.env.PUBLIC_URL + '/AR-Dev.jpg'} alt="Logo" />
              <span>AR-DEV</span>
            </div>
            <p className="footer-tagline">{t("my_desc").substring(0, 100)}...</p>
            <div className="footer-social">
              {data.socialLinks.map((link, index) => (
                <a href={link.url} target="_blank" rel="noopener noreferrer" key={index} className="footer-social-link">
                  <FontAwesomeIcon icon={link.icon} />
                </a>
              ))}
            </div>
          </div>

          <div className="footer-nav-group">
            <h4 className="footer-nav-title">{t("resume")}</h4>
            <ul className="footer-nav-links">
              <li><a href="#services">{t("services")}</a></li>
              <li><a href="#recent-works">{t("works")}</a></li>
              <li><a href="#experience-education">{t("resume")}</a></li>
              <li><a href="#skills-section">{t("skills")}</a></li>
              <li><a href="#contact">{t("contact")}</a></li>
            </ul>
          </div>

          <div className="footer-nav-group">
            <h4 className="footer-nav-title">{t("contact")}</h4>
            <ul className="footer-contact-info">
              <li>{data.contact.email}</li>
              <li>{data.contact.phone}</li>
              <li>{data.contact.address}</li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p>© 2025 {t("name")}. {lang === "fr" ? "Tous droits réservés" : lang === "ar" ? "جميع الحقوق محفوظة" : "All rights reserved"}.</p>
          <div className="footer-legal">
            <span>Built with React & Framer Motion</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
const App = () => {
  const [lang, setLang] = useState("fr");
  const [theme, setTheme] = useState("dark");
  const t = (key) => translations[lang][key] || key;

  useEffect(() => {
    if (theme === 'light') {
      document.body.classList.add('light-mode');
    } else {
      document.body.classList.remove('light-mode');
    }
  }, [theme]);

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  return (
    <div className="app" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <Header
        data={MY_PORTFOLIO_DATA}
        t={t}
        lang={lang}
        setLang={setLang}
        theme={theme}
        toggleTheme={toggleTheme}
      />
      <Services data={MY_PORTFOLIO_DATA} t={t} lang={lang} />
      <RecentWorksGrid data={MY_PORTFOLIO_DATA} t={t} lang={lang} />
      <ExperienceEducation data={MY_PORTFOLIO_DATA} t={t} lang={lang} />
      <Skills data={MY_PORTFOLIO_DATA} t={t} lang={lang} />
      <Languages data={MY_PORTFOLIO_DATA} t={t} />
      <Contact data={MY_PORTFOLIO_DATA} t={t} lang={lang} />
      <Footer data={MY_PORTFOLIO_DATA} t={t} lang={lang} />
    </div>
  );
};

export default App;